import { todayICT } from '@/lib/metricWindow';
import type {
  HistoricalBucket,
  HistoricalBrandCode,
  HistoricalFilters,
  HistoricalRange,
  HistoricalRangeMode,
  HistoricalRangeParams,
  HistoricalWeekdayProfileRow,
  HistoricalValidationCode,
} from '@/api';

export const HISTORICAL_RANGE_MAX_DAYS = 366;

export const HISTORICAL_WEEKDAY_LABELS_TH = ['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.'] as const;

export const HISTORICAL_BRAND_CODES = ['lucky_suki', 'lucky_bbq'] as const satisfies readonly HistoricalBrandCode[];

export const HISTORICAL_VALIDATION_CODES = [
  'invalid_mode', 'invalid_date', 'invalid_range', 'invalid_bucket', 'invalid_brand', 'future_range',
] as const satisfies readonly HistoricalValidationCode[];

/** Validation error with the stable code used by future HTTP 400 responses. */
export class HistoricalRangeValidationError extends Error {
  readonly code: HistoricalValidationCode;
  readonly field: string;

  constructor(code: HistoricalValidationCode, field: string) {
    super(code);
    this.name = 'HistoricalRangeValidationError';
    this.code = code;
    this.field = field;
  }
}

export interface HistoricalRangeQueryResult {
  /** Safe default on invalid input, so a malformed share link can recover. */
  range: HistoricalRange;
  /** Canonical query fields for the returned range. */
  params: HistoricalRangeParams;
  error: HistoricalRangeValidationError | null;
  used_default: boolean;
}

export interface HistoricalQueryResult extends HistoricalRangeQueryResult {
  filters: HistoricalFilters;
}

export interface HistoricalBucketWindow {
  from: string;
  to: string;
  natural_from: string;
  natural_to: string;
  partial: boolean;
}

export interface HistoricalUtcBounds {
  /** Inclusive ICT from at midnight, represented as a UTC instant. */
  start: string;
  /** Exclusive UTC instant immediately after effective_to in ICT. */
  end: string;
}

type HistoricalQueryInput = Record<string, unknown> | URLSearchParams;

/**
 * Return the current ICT week with a nominal Monday–Sunday range.
 *
 * The current week is allowed to run past today. In that case effective_to is
 * today and range_partial is true; the nominal URL never silently changes.
 */
export function defaultHistoricalRange(today = todayICT()): HistoricalRange {
  if (!isHistoricalDate(today)) {
    throw new HistoricalRangeValidationError('invalid_date', 'today');
  }
  const monday = shiftHistoricalDate(today, -weekdayIndex(parseHistoricalDate(today)));
  return resolveHistoricalRange({ mode: 'week', from: monday, to: shiftHistoricalDate(monday, 6), bucket: 'day' }, today);
}

/** Return whether a value is a real, zero-padded YYYY-MM-DD calendar date. */
export function isHistoricalDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;

  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = month === 2
    ? leapYear ? 29 : 28
    : [4, 6, 9, 11].includes(month) ? 30 : 31;
  return day <= daysInMonth;
}

/** Validate one explicit historical range without changing MetricWindow rules. */
export function validateHistoricalRange(
  params: HistoricalRangeParams,
  today = todayICT(),
): HistoricalRangeValidationError | null {
  try {
    resolveHistoricalRange(params, today);
    return null;
  } catch (error) {
    if (error instanceof HistoricalRangeValidationError) return error;
    throw error;
  }
}

/** Resolve validated query fields into the response metadata contract. */
export function resolveHistoricalRange(
  params: HistoricalRangeParams,
  today = todayICT(),
): HistoricalRange {
  if (!isHistoricalDate(today)) throw new HistoricalRangeValidationError('invalid_date', 'today');
  if (!isHistoricalMode(params.mode)) throw new HistoricalRangeValidationError('invalid_mode', 'mode');
  if (!isHistoricalBucket(params.bucket)) throw new HistoricalRangeValidationError('invalid_bucket', 'bucket');
  if (!isHistoricalDate(params.from)) throw new HistoricalRangeValidationError('invalid_date', 'from');
  if (!isHistoricalDate(params.to)) throw new HistoricalRangeValidationError('invalid_date', 'to');

  const from = parseHistoricalDate(params.from);
  const to = parseHistoricalDate(params.to);
  const current = parseHistoricalDate(today);
  if (from > to) throw new HistoricalRangeValidationError('invalid_range', 'range');

  const days = inclusiveHistoricalDateSpan(from, to);
  if (days > HISTORICAL_RANGE_MAX_DAYS) throw new HistoricalRangeValidationError('invalid_range', 'range');

  if (params.mode !== 'custom' && params.bucket !== 'day') {
    throw new HistoricalRangeValidationError('invalid_bucket', 'bucket');
  }
  if (!isCanonicalHistoricalPeriod(params.mode, from, to)) {
    throw new HistoricalRangeValidationError('invalid_range', 'range');
  }

  if (params.mode === 'day' || params.mode === 'custom') {
    if (to > current) throw new HistoricalRangeValidationError('future_range', 'to');
  } else if (from > current) {
    throw new HistoricalRangeValidationError('future_range', 'range');
  }

  const effectiveTo = (params.mode === 'week' || params.mode === 'month') && to > current
    ? today
    : params.to;
  const buckets = historicalBucketWindows(params);
  return {
    mode: params.mode,
    from: params.from,
    to: params.to,
    bucket: params.bucket,
    days,
    effective_to: effectiveTo,
    range_partial: effectiveTo !== params.to,
    bucket_partial: buckets.some((bucket) => bucket.partial),
  };
}

/**
 * Parse a route query. No range fields means the current ICT week; an
 * explicit malformed range returns the safe default plus a stable error.
 */
export function parseHistoricalRangeQuery(
  query: HistoricalQueryInput,
  today = todayICT(),
): HistoricalRangeQueryResult {
  const fallbackToday = isHistoricalDate(today) ? today : todayICT();
  const fallback = defaultHistoricalRange(fallbackToday);
  const hasRangeField = ['mode', 'from', 'to', 'bucket'].some((key) => queryHas(query, key));
  if (!hasRangeField) {
    return { range: fallback, params: rangeToParams(fallback), error: null, used_default: true };
  }

  const modeValue = queryValue(query, 'mode');
  if (!modeValue.present || modeValue.duplicate || modeValue.value === '' || !isHistoricalModeValue(modeValue.value)) {
    return rangeParseFailure(fallback, new HistoricalRangeValidationError('invalid_mode', 'mode'));
  }

  const bucketValue = queryValue(query, 'bucket');
  const bucket = !bucketValue.present
    ? 'day'
    : bucketValue.value as string;
  if (bucketValue.duplicate || !isHistoricalBucketValue(bucket)) {
    return rangeParseFailure(fallback, new HistoricalRangeValidationError('invalid_bucket', 'bucket'));
  }

  const fromValue = queryValue(query, 'from');
  const toValue = queryValue(query, 'to');
  if (!fromValue.present || fromValue.duplicate || fromValue.value === '') {
    return rangeParseFailure(fallback, new HistoricalRangeValidationError('invalid_date', 'from'));
  }
  if (!toValue.present || toValue.duplicate || toValue.value === '') {
    return rangeParseFailure(fallback, new HistoricalRangeValidationError('invalid_date', 'to'));
  }

  const params: HistoricalRangeParams = {
    mode: modeValue.value as HistoricalRangeMode,
    from: fromValue.value,
    to: toValue.value,
    bucket: bucket as HistoricalBucket,
  };
  const error = validateHistoricalRange(params, fallbackToday);
  if (error) return rangeParseFailure(fallback, error);

  return {
    range: resolveHistoricalRange(params, fallbackToday),
    params,
    error: null,
    used_default: false,
  };
}

/** Parse range plus the reserved brand/region/branch query filters. */
export function parseHistoricalQuery(
  query: HistoricalQueryInput,
  today = todayICT(),
): HistoricalQueryResult {
  const rangeResult = parseHistoricalRangeQuery(query, today);
  if (rangeResult.error) return { ...rangeResult, filters: recoverableHistoricalFilters(query) };

  const brandValue = queryValue(query, 'brand');
  if (brandValue.duplicate || (brandValue.value !== '' && !isHistoricalBrand(brandValue.value))) {
    return {
      ...rangeParseFailure(rangeResult.range, new HistoricalRangeValidationError('invalid_brand', 'brand')),
      filters: {},
    };
  }

  return {
    ...rangeResult,
    filters: {
      ...(brandValue.value ? { brand: brandValue.value as HistoricalBrandCode } : {}),
      ...optionalFilter(query, 'region'),
      ...optionalFilter(query, 'branch'),
    },
  };
}

/** Keep valid scope filters when a malformed range falls back to the safe week. */
function recoverableHistoricalFilters(query: HistoricalQueryInput): HistoricalFilters {
  const brandValue = queryValue(query, 'brand');
  return {
    ...(brandValue.present && !brandValue.duplicate && isHistoricalBrand(brandValue.value)
      ? { brand: brandValue.value }
      : {}),
    ...optionalFilter(query, 'region'),
    ...optionalFilter(query, 'branch'),
  };
}

/** Return canonical range fields for router state or an API query builder. */
export function historicalRangeToParams(range: HistoricalRange | HistoricalRangeParams): HistoricalRangeParams {
  return rangeToParams(range);
}

/**
 * Shift a selected period by its natural unit without changing its shape.
 * Validation of the resulting range is intentionally left to the caller so a
 * next button can disable a future result without changing the current URL.
 */
export function shiftHistoricalRange(
  range: Pick<HistoricalRange, 'mode' | 'from' | 'to' | 'bucket' | 'days'>,
  direction: -1 | 1,
): HistoricalRangeParams {
  const from = parseHistoricalDate(range.from);
  const to = parseHistoricalDate(range.to);
  if (range.mode === 'day') {
    const next = addDays(from, direction);
    const date = formatHistoricalDate(next);
    return { mode: 'day', from: date, to: date, bucket: 'day' };
  }
  if (range.mode === 'week') {
    const nextFrom = addDays(from, direction * 7);
    return {
      mode: 'week',
      from: formatHistoricalDate(nextFrom),
      to: formatHistoricalDate(addDays(nextFrom, 6)),
      bucket: 'day',
    };
  }
  if (range.mode === 'month') {
    const nextFrom = addMonths(from, direction);
    const nextTo = addDays(addMonths(nextFrom, 1), -1);
    return {
      mode: 'month',
      from: formatHistoricalDate(nextFrom),
      to: formatHistoricalDate(nextTo),
      bucket: 'day',
    };
  }

  // Shift by the inclusive number of dates. Using the distance between the
  // endpoints would make adjacent custom windows overlap on one date.
  const span = Math.max(1, range.days);
  const nextFrom = addDays(from, direction * span);
  const nextTo = addDays(to, direction * span);
  return {
    mode: 'custom',
    from: formatHistoricalDate(nextFrom),
    to: formatHistoricalDate(nextTo),
    bucket: range.bucket,
  };
}

/** Build a deterministic query string without a leading question mark. */
export function buildHistoricalRangeQuery(
  range: HistoricalRange | HistoricalRangeParams,
  filters: HistoricalFilters = {},
): string {
  const params = rangeToParams(range);
  const brand = (filters.brand ?? '').trim();
  const region = (filters.region ?? '').trim();
  const branch = (filters.branch ?? '').trim();
  if (brand !== '' && !isHistoricalBrand(brand)) {
    throw new HistoricalRangeValidationError('invalid_brand', 'brand');
  }

  const search = new URLSearchParams();
  search.set('mode', params.mode);
  search.set('from', params.from);
  search.set('to', params.to);
  search.set('bucket', params.bucket);
  if (brand) search.set('brand', brand);
  if (region) search.set('region', region);
  if (branch) search.set('branch', branch);
  return search.toString();
}

/** Return the inclusive ICT range as a half-open UTC interval. */
export function historicalRangeUtcBounds(range: Pick<HistoricalRange, 'from' | 'effective_to'>): HistoricalUtcBounds {
  const start = parseHistoricalDate(range.from);
  const end = parseHistoricalDate(shiftHistoricalDate(range.effective_to, 1));
  return {
    start: formatUtcInstant(start, -7),
    end: formatUtcInstant(end, -7),
  };
}

/** Return clipped display buckets for a historical range. */
export function historicalBucketWindows(
  range: Pick<HistoricalRangeParams, 'mode' | 'from' | 'to' | 'bucket'>,
): HistoricalBucketWindow[] {
  if (!isHistoricalDate(range.from) || !isHistoricalDate(range.to) || range.from > range.to) return [];
  const from = parseHistoricalDate(range.from);
  const to = parseHistoricalDate(range.to);
  if (range.mode !== 'custom' || range.bucket === 'day') return dailyHistoricalBuckets(from, to);

  if (range.bucket === 'week') return clippedHistoricalWeekBuckets(from, to);
  if (range.bucket === 'month') return clippedHistoricalMonthBuckets(from, to);
  return [];
}

/** Return the fixed Monday–Sunday weekday coverage skeleton for a range. */
export function historicalWeekdayProfileSkeleton(
  range: Pick<HistoricalRange, 'from' | 'to'>,
  expectedBranches: number,
): HistoricalWeekdayProfileRow[] {
  const rows: HistoricalWeekdayProfileRow[] = HISTORICAL_WEEKDAY_LABELS_TH.map((label, index) => ({
    weekday: (index + 1) as HistoricalWeekdayProfileRow['weekday'],
    label_th: label,
    sales_total: null,
    sales_average: null,
    expected_dates: 0,
    observed_dates: 0,
    complete_dates: 0,
    reporting_branch_days: 0,
    expected_branch_days: 0,
  }));
  if (!isHistoricalDate(range.from) || !isHistoricalDate(range.to) || range.from > range.to) return rows;

  const branchCount = Math.max(0, expectedBranches);
  for (let date = parseHistoricalDate(range.from); date <= parseHistoricalDate(range.to); date = addDays(date, 1)) {
    rows[weekdayIndex(date)]!.expected_dates += 1;
  }
  for (const row of rows) row.expected_branch_days = row.expected_dates * branchCount;
  return rows;
}

/** Apply the additive weekday-average formula; no observed dates means null. */
export function historicalWeekdayAverage(total: number, observedDates: number): number | null {
  return observedDates > 0 ? total / observedDates : null;
}

function resolveHistoricalRangeFailure(
  fallback: HistoricalRange,
  error: HistoricalRangeValidationError,
): HistoricalRangeQueryResult {
  return { range: fallback, params: rangeToParams(fallback), error, used_default: true };
}

const rangeParseFailure = resolveHistoricalRangeFailure;

function rangeToParams(range: HistoricalRange | HistoricalRangeParams): HistoricalRangeParams {
  return {
    mode: range.mode,
    from: range.from,
    to: range.to,
    bucket: range.bucket,
  };
}

function isHistoricalMode(mode: string): mode is HistoricalRangeMode {
  return isHistoricalModeValue(mode);
}

function isHistoricalModeValue(value: string): value is HistoricalRangeMode {
  return value === 'day' || value === 'week' || value === 'month' || value === 'custom';
}

function isHistoricalBucket(value: string): value is HistoricalBucket {
  return isHistoricalBucketValue(value);
}

function isHistoricalBucketValue(value: string): value is HistoricalBucket {
  return value === 'day' || value === 'week' || value === 'month';
}

function isHistoricalBrand(value: string): value is HistoricalBrandCode {
  return value === 'lucky_suki' || value === 'lucky_bbq';
}

function isCanonicalHistoricalPeriod(mode: HistoricalRangeMode, from: Date, to: Date): boolean {
  switch (mode) {
    case 'day':
      return sameHistoricalDate(from, to);
    case 'week':
      return weekdayIndex(from) === 0 && sameHistoricalDate(to, addDays(from, 6));
    case 'month': {
      if (from.getUTCDate() !== 1) return false;
      const nextMonth = addMonths(from, 1);
      return sameHistoricalDate(to, addDays(nextMonth, -1));
    }
    case 'custom':
      return true;
  }
}

function queryHas(query: HistoricalQueryInput, key: string): boolean {
  if (query instanceof URLSearchParams) return query.has(key);
  return Object.prototype.hasOwnProperty.call(query, key);
}

function queryValue(query: HistoricalQueryInput, key: string): { value: string; present: boolean; duplicate: boolean } {
  if (query instanceof URLSearchParams) {
    const values = query.getAll(key);
    return { value: (values[0] ?? '').trim(), present: values.length > 0, duplicate: values.length > 1 };
  }
  if (!Object.prototype.hasOwnProperty.call(query, key)) return { value: '', present: false, duplicate: false };
  const value = query[key];
  if (Array.isArray(value)) return { value: String(value[0] ?? '').trim(), present: true, duplicate: value.length > 1 };
  return { value: String(value ?? '').trim(), present: true, duplicate: false };
}

function optionalFilter(query: HistoricalQueryInput, key: 'region' | 'branch'): HistoricalFilters {
  const value = queryValue(query, key);
  if (!value.present || value.duplicate || value.value === '') return {};
  return { [key]: value.value };
}

function parseHistoricalDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(Number(match![1]), Number(match![2]) - 1, Number(match![3]));
  return date;
}

function formatHistoricalDate(date: Date): string {
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date.getTime());
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function shiftHistoricalDate(value: string, days: number): string {
  return formatHistoricalDate(addDays(parseHistoricalDate(value), days));
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime());
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  return result;
}

function sameHistoricalDate(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate();
}

function inclusiveHistoricalDateSpan(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
}

/** Monday is index 0; Sunday is index 6. */
function weekdayIndex(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function dailyHistoricalBuckets(from: Date, to: Date): HistoricalBucketWindow[] {
  const buckets: HistoricalBucketWindow[] = [];
  for (let date = from; date <= to; date = addDays(date, 1)) {
    const value = formatHistoricalDate(date);
    buckets.push({ from: value, to: value, natural_from: value, natural_to: value, partial: false });
  }
  return buckets;
}

function clippedHistoricalWeekBuckets(from: Date, to: Date): HistoricalBucketWindow[] {
  const buckets: HistoricalBucketWindow[] = [];
  let naturalFrom = addDays(from, -weekdayIndex(from));
  for (; naturalFrom <= to; naturalFrom = addDays(naturalFrom, 7)) {
    const naturalTo = addDays(naturalFrom, 6);
    const clippedFrom = from > naturalFrom ? from : naturalFrom;
    const clippedTo = to < naturalTo ? to : naturalTo;
    buckets.push({
      from: formatHistoricalDate(clippedFrom),
      to: formatHistoricalDate(clippedTo),
      natural_from: formatHistoricalDate(naturalFrom),
      natural_to: formatHistoricalDate(naturalTo),
      partial: !sameHistoricalDate(clippedFrom, naturalFrom) || !sameHistoricalDate(clippedTo, naturalTo),
    });
  }
  return buckets;
}

function clippedHistoricalMonthBuckets(from: Date, to: Date): HistoricalBucketWindow[] {
  const buckets: HistoricalBucketWindow[] = [];
  let naturalFrom = new Date(from.getTime());
  naturalFrom.setUTCDate(1);
  for (; naturalFrom <= to; naturalFrom = addMonths(naturalFrom, 1)) {
    const naturalTo = addDays(addMonths(naturalFrom, 1), -1);
    const clippedFrom = from > naturalFrom ? from : naturalFrom;
    const clippedTo = to < naturalTo ? to : naturalTo;
    buckets.push({
      from: formatHistoricalDate(clippedFrom),
      to: formatHistoricalDate(clippedTo),
      natural_from: formatHistoricalDate(naturalFrom),
      natural_to: formatHistoricalDate(naturalTo),
      partial: !sameHistoricalDate(clippedFrom, naturalFrom) || !sameHistoricalDate(clippedTo, naturalTo),
    });
  }
  return buckets;
}

function formatUtcInstant(date: Date, offsetHours: number): string {
  const instant = new Date(date.getTime() + offsetHours * 3_600_000);
  return instant.toISOString().replace('.000Z', 'Z');
}
